import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  List,
  ListItem,
} from "@chakra-ui/react";
import { GaraazInput } from "@svan-garaaz/reactsharedlib";
import { resolveAndFormatDate } from "../../../utils/dateResolvers";

export type ParentOrderInvoiceInfo = {
  invoiceNo?: string;
  invoiceAmount?: string;
  invoiceDate?: string;
};

type AcceptReturnCreditNoteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (creditNoteAmount: string) => Promise<void>;
  parentOrderInvoiceInfo?: ParentOrderInvoiceInfo;
};

const AcceptReturnCreditNoteAmountModal: React.FC<
  AcceptReturnCreditNoteModalProps
> = ({ isOpen, onClose, onSubmit, parentOrderInvoiceInfo }) => {
  const [amount, setAmount] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setError(null);
    }
  }, [isOpen]);

  const maxInvoiceAmount = Number(parentOrderInvoiceInfo?.invoiceAmount) || 0;

  const handleAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    _name?: string,
  ) => {
    let raw = e.target.value;
    raw = raw.replace(/[^0-9.]/g, "");
    const parts = raw.split(".");
    const sanitized =
      parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : raw;
    setAmount(sanitized);
    const num = Number(sanitized);
    let err: string | null = null;
    if (sanitized !== "") {
      if (num <= 0 || isNaN(num)) {
        err = "Amount must be greater than 0";
      } else if (num > maxInvoiceAmount) {
        err = `Amount cannot exceed invoice amount (₹${maxInvoiceAmount})`;
      }
    }
    setError(err);
  };

  const numAmount = Number(amount);
  const isValid =
    amount !== "" && numAmount > 0 && numAmount <= maxInvoiceAmount && !error;

  const handleConfirm = async () => {
    if (!isValid) return;
    try {
      setIsSubmitting(true);
      await onSubmit(amount);
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Enter Return Credit Note Amount</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              A CN will be created against the parent order's invoice for the
              entered amount.{" "}
              <b>Please verify the amount carefully before proceeding.</b>
            </Text>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                Parent Order Invoice Info:
              </Text>
              <List spacing={1} fontSize="sm" color="gray.600">
                <ListItem>
                  Number: {parentOrderInvoiceInfo?.invoiceNo || "---"}
                </ListItem>
                <ListItem>
                  Amount:{" "}
                  {parentOrderInvoiceInfo?.invoiceAmount
                    ? `₹${parentOrderInvoiceInfo.invoiceAmount}`
                    : "---"}
                </ListItem>
                <ListItem>
                  Date:{" "}
                  {parentOrderInvoiceInfo?.invoiceDate
                    ? resolveAndFormatDate(parentOrderInvoiceInfo.invoiceDate)
                    : "---"}
                </ListItem>
              </List>
            </Box>
            <Box>
              <GaraazInput
                width="100%"
                inputName="creditNoteAmount"
                label=""
                value={amount}
                placeholder="Enter credit note amount"
                inputType="text"
                onChange={handleAmountChange}
                isRequired={false}
              />
              {error && (
                <Text fontSize="sm" color="red.500" mt={2}>
                  {error}
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isDisabled={!isValid}
              isLoading={isSubmitting}
            >
              Confirm
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AcceptReturnCreditNoteAmountModal;
